import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Scale, MessageCircle } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, STEPS, REVIEWS } from "./mayfairData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GOLDY = "#D4A017";
const BLACK = "#0F0F0F";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function MayfairSite() {
  const [open, setOpen] = useState(false);
  const links = [["Value", "#value"], ["Ready Stock", "#products"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#111", fontFamily: "'Archivo', sans-serif" }} data-testid="mayfair-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;700;900&display=swap');`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: GOLDY }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b-2 border-black">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="mayfair-logo" className="font-black text-2xl tracking-tight">MAYFAIR</a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-bold uppercase tracking-wide text-black/70 hover:text-black">{l}</a>)}
          </div>
          <a href="#visit" data-testid="mayfair-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-bold text-white" style={{ background: BLACK }}>Visit the Shop</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mayfair-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-bold">{l}</a>)}</div>}
      </header>

      {/* HERO — bold, punchy, value-forward */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="mayfair-hero">
        <img src="/mayfair/logo-card.jpg" alt="Mayfair Goldsmith" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "68% center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,.94) 0%, rgba(0,0,0,.82) 42%, rgba(0,0,0,.28) 72%, rgba(0,0,0,.08) 100%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl border-l-4 pl-5 sm:pl-7" style={{ borderColor: GOLDY }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-bold uppercase" style={{ background: GOLDY, color: BLACK }}><Scale size={13} />Weighed in front of you</span>
            <h1 className="mt-6 text-5xl sm:text-6xl font-black text-white leading-[1.02]">Priced by Weight.<br />Ready to Wear.</h1>
            <p className="mt-6 text-white/75 max-w-md">Competitively priced Ongpin gold, weighed transparently and customized fast.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#products" data-testid="mayfair-hero-cta" className="px-8 py-3.5 text-sm font-bold" style={{ background: GOLDY, color: BLACK }}>See Ready Stock</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="mayfair-hero-message" className="px-8 py-3.5 text-sm font-bold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-8 text-sm text-white/50">{BIZ.rating}★ Rating · {BIZ.reviewCount} Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROP — trust mechanic strip, not icon circles */}
      <section id="value" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid sm:grid-cols-4 border-t-2 border-b-2 border-black">
          {HIGHLIGHTS.map((h, i) => (
            <motion.div key={h.title} {...fadeUp} className={`py-8 px-4 text-center ${i > 0 ? "sm:border-l-2 border-black" : ""}`}>
              <p className="font-black text-sm uppercase">{h.title}</p>
              <p className="mt-2 text-xs text-black/60">{h.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* READY STOCK */}
      <section id="products" className="py-20" style={{ background: "#F7F1E0" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-4xl font-black text-center mb-12">Ready-Stock Designs</motion.h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="mayfair-product-card" className="bg-white group">
                <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4">
                  <h3 className="font-bold text-sm">{p.name}</h3>
                  <p className="mt-1 text-xs text-black/55">{p.desc}</p>
                  <p className="mt-3 text-xs font-bold" style={{ color: "#8A6B0A" }}>Ask About This Piece &rarr;</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — weight-mechanic strip */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="text-3xl font-black text-center mb-12">How the Weighing Works</motion.h2>
        <div className="grid sm:grid-cols-4 gap-px bg-black">
          {STEPS.map((s) => (
            <motion.div key={s.n} {...fadeUp} className="bg-white p-6 text-center">
              <div className="font-black text-2xl" style={{ color: GOLDY }}>{s.n}</div>
              <p className="mt-2 font-bold text-sm">{s.title}</p>
              <p className="mt-1 text-xs text-black/55">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="py-20" style={{ background: BLACK }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-4xl font-black text-white text-center mb-12">What Customers Say</motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {REVIEWS.map((r) => (
              <motion.div key={r.name} {...fadeUp} className="p-6" style={{ background: "#1A1A1A" }}>
                <p className="text-white/80 text-sm leading-relaxed">"{r.text}"</p>
                <p className="mt-4 text-xs font-bold" style={{ color: GOLDY }}>{r.name}</p>
                <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-white/40 underline underline-offset-2">{r.source}</a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="text-4xl font-black">Visit the Shop</h2>
          <ul className="mt-8 space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GOLDY }} className="mt-0.5" />{BIZ.address}</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: GOLDY }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GOLDY }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="mayfair-facebook-link" className="hover:underline">mayfair.goldsmith</a></li>
          </ul>
          <img src={BIZ.logoCard} alt="Mayfair Goldsmith contact card" className="mt-8 w-full max-w-sm shadow-md" />
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px]"><iframe title="Mayfair Goldsmith map" src={BIZ.mapEmbed} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
      </section>

      <footer className="py-8 bg-white border-t-2 border-black">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-black/55">
          <p className="font-black text-base">MAYFAIR GOLDSMITH</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="mayfair-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold shadow-xl flex items-center justify-center gap-2" style={{ background: GOLDY, color: BLACK }}><MessageCircle size={17} /> Message Mayfair</a>
      <ConceptCTAPill slug="mayfair-goldsmith-concept" />
    </div>
  );
}
